import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import howItWorksSearch from '@/assets/how_it_works/how_it_works_search.png';
import howItWorksStudy from '@/assets/how_it_works/how_it_works_study.png';
import howItWorksResult from '@/assets/how_it_works/how_it_works_result.png';
import { useI18n } from '@/i18n';

const steps = [
  {
    number: '1',
    title: 'Find your tutor.',
    description:
      "Tell us what you need help with and we'll match you with the best tutor.",
    badgeClass: 'bg-[#10B35B] text-white',
    imageSrc: howItWorksSearch,
    imageAlt: 'Search and find your tutor',
  },
  {
    number: '2',
    title: 'Start learning.',
    description:
      'Your tutor will tailor every lesson to your learning style and help you build confidence.',
    badgeClass: 'bg-[#FFC62A] text-white',
    imageSrc: howItWorksStudy,
    imageAlt: 'Start learning with your tutor',
  },
  {
    number: '3',
    title: 'Make progress every week.',
    description:
      'Track your progress, stay motivated, and achieve your academic goals.',
    badgeClass: 'bg-[#2E85C7] text-white',
    imageSrc: howItWorksResult,
    imageAlt: 'Track your weekly progress',
  },
];

const musicSteps = [
  {
    number: '1',
    title: 'Tell us what you want to learn.',
    description: 'Share your instrument, current level, location, and preferred lesson schedule.',
    badgeClass: 'bg-gray-900 text-white',
    imageSrc: howItWorksSearch,
    imageAlt: 'Share your music learning goals',
  },
  {
    number: '2',
    title: 'Meet your music tutor.',
    description: 'Connect with a tutor whose experience and teaching style match your goals.',
    badgeClass: 'bg-primary text-white',
    imageSrc: howItWorksStudy,
    imageAlt: 'Learn with a music tutor',
  },
  {
    number: '3',
    title: 'Grow with every lesson.',
    description: 'Build technique, confidence, and musical expression at a pace that works for you.',
    badgeClass: 'border border-primary-200 bg-primary-50 text-primary-900',
    imageSrc: howItWorksResult,
    imageAlt: 'Make progress in music lessons',
  },
];

const cardVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.55 },
  },
};

interface ProcessSectionProps {
  variant?: 'academic' | 'music';
}

const ProcessSection: React.FC<ProcessSectionProps> = ({ variant = 'academic' }) => {
  const { locale } = useI18n();
  const isZh = locale === 'zh-CN';
  const academicSteps = isZh
    ? [
        { number: '1', title: '找到适合你的导师。', description: '告诉我们你的学习需求，我们会为你配对最合适的导师。', badgeClass: 'bg-[#10B35B] text-white', imageSrc: howItWorksSearch, imageAlt: '寻找导师' },
        { number: '2', title: '开始学习。', description: '导师会按你的学习方式调整每一堂课，帮助你建立信心。', badgeClass: 'bg-[#FFC62A] text-white', imageSrc: howItWorksStudy, imageAlt: '开始上课' },
        { number: '3', title: '每周稳步进步。', description: '持续追踪学习成果，保持动力，并一步步达成学业目标。', badgeClass: 'bg-[#2E85C7] text-white', imageSrc: howItWorksResult, imageAlt: '每周进步' },
      ]
    : steps;
  const displayedSteps = variant === 'music' ? musicSteps : academicSteps;
  useEffect(() => {
    if (!document.getElementById('landing-process-swiper-styles')) {
      const style = document.createElement('style');
      style.id = 'landing-process-swiper-styles';
      style.textContent = `
        .landing-process-swiper {
          padding-bottom: 38px !important;
          padding-right: 20px !important;
        }
        .landing-process-swiper .swiper-pagination {
          position: absolute !important;
          bottom: 0 !important;
          text-align: center;
        }
        .landing-process-swiper .swiper-pagination-bullet {
          width: 8px !important;
          height: 8px !important;
          background-color: rgb(var(--brand-carousel)) !important;
          opacity: 0.35 !important;
          margin: 0 4px !important;
          transition: all 0.3s ease !important;
          border-radius: 50% !important;
        }
        .landing-process-swiper .swiper-pagination-bullet-active {
          width: 24px !important;
          height: 8px !important;
          border-radius: 4px !important;
          opacity: 1 !important;
        }
        .landing-process-swiper .swiper-slide {
          height: auto !important;
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  const renderStepCard = (step: typeof steps[number], index?: number, isMobile = false) => (
    <motion.article
      key={step.number}
      initial={isMobile ? false : 'hidden'}
      whileInView={isMobile ? undefined : 'visible'}
      viewport={isMobile ? undefined : { once: true, amount: 0.2 }}
      variants={cardVariants}
      transition={index !== undefined ? { delay: index * 0.08 } : undefined}
      className="flex h-full flex-col rounded-[2rem] border border-gray-200 bg-white p-5 shadow-[0_18px_44px_rgba(15,23,42,0.06)] md:p-6"
    >
      <div className={`inline-flex h-16 w-16 items-center justify-center rounded-2xl text-4xl font-bold shadow-sm ${step.badgeClass}`}>
        {step.number}
      </div>

      <div className={`mt-5 overflow-hidden rounded-[1.75rem] ${variant === 'music' ? 'bg-primary-50' : 'bg-[#F8FBF9]'}`}>
        <img
          src={step.imageSrc}
          alt={step.imageAlt}
          className="aspect-[4/3] w-full object-cover"
        />
      </div>

      <div className="mt-6 flex flex-1 flex-col">
        <h3 className="text-[2rem] font-semibold leading-tight tracking-tight text-gray-900">
          {step.title}
        </h3>
        <p className="mt-4 min-h-[7.5rem] max-w-[20ch] text-xl leading-relaxed text-gray-600 md:min-h-[8rem] md:max-w-none">
          {step.description}
        </p>
      </div>
    </motion.article>
  );

  return (
    <section className="bg-white py-14 md:py-20">
      <div className="container mx-auto max-w-7xl px-4">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true, amount: 0.4 }}
          className="mx-auto mb-10 max-w-2xl text-center md:mb-14"
        >
          <h2 className={`text-4xl font-bold tracking-tight md:text-5xl ${variant === 'music' ? 'text-gray-900' : 'text-primary'}`}>
            {variant === 'music' ? 'How it works' : isZh ? '使用方式' : 'How it works'}
          </h2>
        </motion.div>

        <div className="md:hidden">
          <Swiper
            modules={[Pagination]}
            pagination={{ clickable: true }}
            spaceBetween={16}
            slidesPerView={1.08}
            className="landing-process-swiper"
          >
            {displayedSteps.map((step) => (
              <SwiperSlide key={step.number}>
                {renderStepCard(step, undefined, true)}
              </SwiperSlide>
            ))}
          </Swiper>
        </div>

        <div className="hidden gap-6 md:grid lg:grid-cols-3">
          {displayedSteps.map((step, index) => renderStepCard(step, index))}
        </div>
      </div>
    </section>
  );
};

export default ProcessSection;
